import React from 'react'
import PropTypes from 'prop-types'
import { toZonedTime } from 'date-fns-tz'
import { formatUtcToLocal } from '../../utils/format'
import { findMatchBetweenPlayers, getHomeAwayStatus } from '../../utils/match'
import AvailabilityAddButton from './AvailabilityAddButton'
import './OpponentAvailabilityListView.css'

// Detect user's timezone
const TZ = Intl.DateTimeFormat().resolvedOptions().timeZone

/**
 * OpponentAvailabilityListView Component
 *
 * A reusable component that displays a list of opponents with their availability slots.
 * Each opponent is shown in a card with their availability times, and includes
 * interactive buttons to propose matches. The component handles various match proposal
 * states including pending, accepted, declined, expired, and scheduled matches.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Array} props.opponents - Array of opponent objects with availability data
 * @param {number} props.opponents[].id - Opponent's player ID
 * @param {number} props.opponents[].user_id - Opponent's user ID
 * @param {string} props.opponents[].first_name - Opponent's first name
 * @param {string} props.opponents[].last_name - Opponent's last name
 * @param {Object} [props.opponents[].team] - Opposing team identity (doubles only).
 *   Shape: { team_id, display_name, players: [{ id, user_id, first_name, last_name }] }.
 *   When `team.display_name` is a non-empty string it is used as the card heading;
 *   otherwise the heading falls back to `${first_name} ${last_name}` (singles).
 * @param {Array} props.opponents[].slots - Processed availability slots for the opponent
 * @param {Function} props.onProposeMatch - Callback function when "Propose Match" is clicked
 *   Signature: (opponent, date, hour, minute, availabilityStart, availabilityEnd) => void
 * @param {Function} props.hasUserOverlap - Function to check if user has overlapping availability
 * @param {Function} props.getMatchProposalForOpponent - Function to get match proposal for opponent/time
 * @param {Function} props.getScheduledMatchForOpponent - Function to get scheduled match for opponent/time
 * @param {Set} [props.pendingProposals] - Set of pending proposal keys (opponentId-proposedTime) for optimistic UI
 * @param {boolean} [props.showEmptyState=true] - Whether to show opponents with no availability
 * @param {string} [props.emptyMessage='No upcoming availability'] - Custom message for empty state
 * @param {number} [props.currentPlayerId] - Current player's ID to determine home/away status
 * @param {Array} [props.allMatches] - All matches in the division to determine home/away relationship
 * @param {boolean} [props.showAddButton=true] - Whether to show the add availability button in the header
 * @param {Function} [props.onAddAvailability] - Callback function when add availability button is clicked
 * @param {string} [props.title='Opponent Availability'] - Title text for the header
 * @param {boolean} [props.directProposalEnabled=false] - Whether to show direct proposal actions
 * @param {Function} [props.onDirectProposeMatch] - Callback for direct match proposal
 * @param {Function} [props.getExistingMatchWithOpponent] - Function to get existing match with opponent
 *
 * @example
 * <OpponentAvailabilityListView
 *   opponents={processedOpponents}
 *   onProposeMatch={(opponent, date, hour, minute) => handlePropose(opponent, date, hour, minute)}
 *   hasUserOverlap={(slot) => checkOverlap(slot)}
 *   getMatchProposalForOpponent={(id, date, hour) => getProposal(id, date, hour)}
 *   getScheduledMatchForOpponent={(id, date, hour) => getMatch(id, date, hour)}
 *   pendingProposals={new Set()}
 *   showEmptyState={true}
 *   emptyMessage="No upcoming availability"
 *   currentPlayerId={123}
 *   allMatches={matches}
 *   showAddButton={true}
 *   onAddAvailability={() => openModal()}
 *   title="Opponent Availability"
 * />
 */
function OpponentAvailabilityListView({
  opponents,
  onProposeMatch,
  hasUserOverlap,
  getMatchProposalForOpponent,
  getLatestProposalForOpponent,
  getScheduledMatchForOpponent,
  pendingProposals = new Set(),
  showEmptyState = true,
  emptyMessage = 'No upcoming availability',
  currentPlayerId,
  allMatches = [],
  showAddButton = true,
  onAddAvailability,
  title = 'Opponent Availability',
  directProposalEnabled = false,
  onDirectProposeMatch,
  getExistingMatchWithOpponent,
}) {
  if (!opponents || opponents.length === 0) {
    return (
      <div className="alert alert-info">
        <p className="mb-0">
          No unplayed opponents found in this league. You may have completed all
          your matches!
        </p>
      </div>
    )
  }

  return (
    <>
      {/* Header with optional add button */}
      <div
        className="opponent-list-header"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem',
        }}
      >
        <h4 style={{ margin: 0 }}>{title}</h4>
        {showAddButton && onAddAvailability && (
          <AvailabilityAddButton
            className="opponent-list-header__add-button"
            onClick={onAddAvailability}
          />
        )}
      </div>

      <div className="opponent-list">
        {opponents.map((opponent) => {
          const slots = opponent.slots || []
          const latestOpponentProposal = getLatestProposalForOpponent
            ? getLatestProposalForOpponent(opponent.id)
            : null
          const hasPendingSentProposal =
            latestOpponentProposal &&
            latestOpponentProposal.status === 'pending' &&
            latestOpponentProposal.is_sent_by_user
          const existingMatch = getExistingMatchWithOpponent
            ? getExistingMatchWithOpponent(opponent.id)
            : null
          const hasExistingMatch = !!existingMatch

          // Determine home/away status for this opponent
          const matchWithOpponent = findMatchBetweenPlayers(
            allMatches,
            currentPlayerId,
            opponent.id,
          )
          const {
            isHome: isCurrentPlayerHome,
            shouldShow: showHomeAwayIndicator,
          } = getHomeAwayStatus(matchWithOpponent, currentPlayerId)

          // If no slots and showEmptyState is false, skip this opponent
          if (slots.length === 0 && !showEmptyState) {
            return null
          }

          // If no slots, show empty state card
          if (slots.length === 0) {
            return (
              <div key={opponent.id} className="card mb-3">
                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-2">
                      <h5 className="card-title mb-0">
                        {opponent.team?.display_name ||
                          `${opponent.first_name} ${opponent.last_name}`}
                      </h5>
                      {showHomeAwayIndicator && (
                        <span
                          className={`home-away-badge ${isCurrentPlayerHome ? 'badge-home' : 'badge-away'}`}
                          title={
                            isCurrentPlayerHome
                              ? 'You host this match'
                              : 'You travel to their court'
                          }
                          aria-label={
                            isCurrentPlayerHome ? 'Home match' : 'Away match'
                          }
                        >
                          {isCurrentPlayerHome ? '🏠 Home' : '🏃 Away'}
                        </span>
                      )}
                    </div>
                    {directProposalEnabled && onDirectProposeMatch && (
                      <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => onDirectProposeMatch(opponent)}
                        disabled={hasPendingSentProposal || hasExistingMatch}
                        data-testid={`direct-proposal-${opponent.id}`}
                      >
                        {hasPendingSentProposal
                          ? 'Proposal Pending'
                          : hasExistingMatch
                            ? 'Match Exists'
                            : 'Propose Time'}
                      </button>
                    )}
                  </div>
                  {!existingMatch && (
                    <p className="text-muted mb-0 mt-2">{emptyMessage}</p>
                  )}
                  {(latestOpponentProposal || existingMatch) && (
                    <div className="mt-2">
                      {existingMatch && (
                        <span className="badge bg-success">
                          {existingMatch.status === 'scheduled'
                            ? '✓ Match Scheduled'
                            : '✓ Match Played'}
                        </span>
                      )}
                      {!existingMatch && latestOpponentProposal && (
                        <>
                          {latestOpponentProposal.status === 'pending' && (
                            <span className="badge bg-primary">
                              {latestOpponentProposal.is_sent_by_user
                                ? '→ Proposal Sent'
                                : '← Proposal Received'}
                            </span>
                          )}
                          {latestOpponentProposal.status === 'declined' && (
                            <span className="badge bg-danger">
                              ✕ Proposal Rejected
                            </span>
                          )}
                          {latestOpponentProposal.status === 'expired' && (
                            <span className="badge bg-warning text-dark">
                              ⚠️ Proposal Expired
                            </span>
                          )}
                          {latestOpponentProposal.proposed_time && (
                            <div className="text-muted mt-1">
                              <strong>Proposed: </strong>
                              {formatUtcToLocal(
                                latestOpponentProposal.proposed_time,
                                'h:mm a',
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )
          }

          // Render opponent card with availability slots
          return (
            <div key={opponent.id} className="card mb-3">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <div className="d-flex align-items-center gap-2">
                    <h5 className="card-title mb-0">
                      {opponent.team?.display_name ||
                        `${opponent.first_name} ${opponent.last_name}`}
                    </h5>
                    {showHomeAwayIndicator && (
                      <span
                        className={`home-away-badge ${isCurrentPlayerHome ? 'badge-home' : 'badge-away'}`}
                        title={
                          isCurrentPlayerHome
                            ? 'You host this match'
                            : 'You travel to their court'
                        }
                        aria-label={
                          isCurrentPlayerHome ? 'Home match' : 'Away match'
                        }
                      >
                        {isCurrentPlayerHome ? '🏠 Home' : '🏃 Away'}
                      </span>
                    )}
                  </div>
                  {directProposalEnabled && onDirectProposeMatch && (
                    <button
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => onDirectProposeMatch(opponent)}
                      data-testid={`direct-proposal-${opponent.id}`}
                    >
                      Propose Time
                    </button>
                  )}
                </div>
                <div className="availability-slots">
                  {slots.map((combinedSlot, index) => {
                    const hasOverlap = hasUserOverlap(combinedSlot)
                    const slotDate = toZonedTime(combinedSlot.start_time, TZ)
                    const slotHour = slotDate.getHours()
                    const slotMinute = slotDate.getMinutes()

                    const proposal = getMatchProposalForOpponent(
                      opponent.id,
                      slotDate,
                      slotHour,
                      combinedSlot.end_time, // Pass the slot end time for proper matching
                    )
                    const scheduledMatch = getScheduledMatchForOpponent(
                      opponent.id,
                      slotDate,
                      slotHour,
                    )

                    // Check if this specific proposal is being submitted (optimistic UI)
                    // Create the same key format used when submitting: opponentId-proposedTime
                    const local = new Date(
                      slotDate.getFullYear(),
                      slotDate.getMonth(),
                      slotDate.getDate(),
                      slotHour,
                      slotMinute,
                    )
                    const proposedTimeISO = local.toISOString()
                    const proposalKey = `${opponent.id}-${proposedTimeISO}`
                    const isPendingSubmission =
                      pendingProposals.has(proposalKey)

                    return (
                      <div
                        key={index}
                        className={`availability-slot mb-2 ${
                          hasOverlap ? 'overlap-highlight' : ''
                        } ${
                          scheduledMatch
                            ? 'scheduled-match-slot'
                            : proposal || isPendingSubmission
                              ? 'proposal-slot'
                              : ''
                        }`}
                      >
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <div className="d-flex align-items-center">
                              <strong>{combinedSlot.date}</strong>
                              {hasOverlap &&
                                !scheduledMatch &&
                                !proposal &&
                                !isPendingSubmission && (
                                  <span className="badge bg-success ms-2 slot-status-badge">
                                    ⚡ Overlapping Availability
                                  </span>
                                )}
                              {scheduledMatch && (
                                <span className="badge bg-success ms-2 slot-status-badge">
                                  ✓ Scheduled Match
                                </span>
                              )}
                              {isPendingSubmission && !proposal && (
                                <span className="badge bg-info ms-2 slot-status-badge">
                                  ⏳ Sending Proposal...
                                </span>
                              )}
                              {proposal &&
                                proposal.status === 'pending' &&
                                !isPendingSubmission && (
                                  <span className="badge bg-primary ms-2 slot-status-badge">
                                    {proposal.is_sent_by_user
                                      ? '→ Proposal Sent'
                                      : '← Proposal Received'}
                                  </span>
                                )}
                              {proposal && proposal.status === 'declined' && (
                                <span className="badge bg-danger ms-2 slot-status-badge">
                                  ✕ Proposal Rejected
                                </span>
                              )}
                              {proposal && proposal.status === 'expired' && (
                                <span className="badge bg-warning text-dark ms-2 slot-status-badge">
                                  ⚠️ Proposal Expired
                                </span>
                              )}
                            </div>
                            <span className="text-muted">
                              {formatUtcToLocal(
                                combinedSlot.start_time,
                                'h:mm a',
                              )}{' '}
                              -{' '}
                              {formatUtcToLocal(
                                combinedSlot.end_time,
                                'h:mm a',
                              )}
                            </span>
                          </div>
                          <div>
                            {scheduledMatch ? (
                              <span className="text-success fw-bold">
                                Match Scheduled
                              </span>
                            ) : proposal && proposal.status === 'pending' ? (
                              <span className="fw-bold text-info">
                                Proposal Pending
                              </span>
                            ) : isPendingSubmission ? (
                              <button
                                className="btn btn-sm btn-secondary"
                                disabled
                              >
                                Sending...
                              </button>
                            ) : (
                              <button
                                className={`btn btn-sm ${
                                  hasOverlap ? 'btn-success' : 'btn-primary'
                                }`}
                                onClick={() => {
                                  onProposeMatch(
                                    opponent,
                                    slotDate,
                                    slotHour,
                                    slotMinute,
                                    combinedSlot.start_time,
                                    combinedSlot.end_time,
                                  )
                                }}
                              >
                                {hasOverlap
                                  ? '⚡ Propose Now'
                                  : 'Propose Match'}
                              </button>
                            )}
                          </div>
                          <span className="text-muted">
                            {proposal &&
                            proposal.status === 'pending' &&
                            proposal.proposed_time ? (
                              <>
                                <strong>Proposed: </strong>
                                {formatUtcToLocal(
                                  proposal.proposed_time,
                                  'h:mm a',
                                )}
                                <span
                                  className="text-muted"
                                  style={{
                                    fontSize: '0.85em',
                                    marginLeft: '4px',
                                  }}
                                >
                                  (window:{' '}
                                  {formatUtcToLocal(
                                    combinedSlot.start_time,
                                    'h:mm a',
                                  )}{' '}
                                  -{' '}
                                  {formatUtcToLocal(
                                    combinedSlot.end_time,
                                    'h:mm a',
                                  )}
                                  )
                                </span>
                              </>
                            ) : scheduledMatch &&
                              scheduledMatch.match_datetime ? (
                              <>
                                <strong>Match Time: </strong>
                                {formatUtcToLocal(
                                  scheduledMatch.match_datetime,
                                  'h:mm a',
                                )}
                              </>
                            ) : (
                              <>
                                {formatUtcToLocal(
                                  combinedSlot.start_time,
                                  'h:mm a',
                                )}{' '}
                                -{' '}
                                {formatUtcToLocal(
                                  combinedSlot.end_time,
                                  'h:mm a',
                                )}
                              </>
                            )}
                          </span>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </>
  )
}

OpponentAvailabilityListView.propTypes = {
  opponents: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      user_id: PropTypes.number,
      first_name: PropTypes.string.isRequired,
      last_name: PropTypes.string.isRequired,
      // Opposing-team identity for doubles; null/undefined for singles.
      team: PropTypes.shape({
        team_id: PropTypes.number,
        display_name: PropTypes.string,
        players: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.number.isRequired,
            user_id: PropTypes.number.isRequired,
            first_name: PropTypes.string.isRequired,
            last_name: PropTypes.string.isRequired,
          }),
        ),
      }),
      slots: PropTypes.arrayOf(
        PropTypes.shape({
          date: PropTypes.string.isRequired,
          start_time: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.instanceOf(Date),
          ]).isRequired,
          end_time: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.instanceOf(Date),
          ]).isRequired,
        }),
      ), // slots array is optional - opponents may have no availability
    }),
  ).isRequired,
  onProposeMatch: PropTypes.func.isRequired,
  hasUserOverlap: PropTypes.func.isRequired,
  getMatchProposalForOpponent: PropTypes.func.isRequired,
  getLatestProposalForOpponent: PropTypes.func,
  getScheduledMatchForOpponent: PropTypes.func.isRequired,
  pendingProposals: PropTypes.instanceOf(Set),
  showEmptyState: PropTypes.bool,
  emptyMessage: PropTypes.string,
  currentPlayerId: PropTypes.number,
  allMatches: PropTypes.array,
  showAddButton: PropTypes.bool,
  onAddAvailability: PropTypes.func,
  title: PropTypes.string,
  directProposalEnabled: PropTypes.bool,
  onDirectProposeMatch: PropTypes.func,
}

export default OpponentAvailabilityListView
